import React, { useEffect, useRef, useState } from "react";
import {
  Grid,
  Typography,
  Breadcrumbs,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Card,
  CardContent,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Chip,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import Link from "@mui/material/Link";
import axios from "axios";
import moment from "moment";
import Layout from "../../layout/Layout";
import { AlertMessage } from "../../components/AlertMessage";
import { config, paymentCashAPI, userData } from "../../config";

// Accountant tool for the cash-in-hand imprest ledger. Pick a site engineer,
// see their balance in hand + ledger, and record a top-up / reimbursement /
// adjustment. Payment debits are written automatically when a cash (no-PO)
// request is paid, so they are not entered here. All calls hit
// POST /payment-request/cash: 1 balance, 2 ledger, 3 add entry, 4 employees.
const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

const ENTRY_TYPES = [
  { value: "topup", label: "Cash float top-up" },
  { value: "reimbursement", label: "Reimbursement" },
  { value: "adjustment", label: "Adjustment (+/-)" },
];

function CashInHand() {
  const childRef = useRef();

  const [employees, setEmployees] = useState([]);
  const [empId, setEmpId] = useState(0);
  const [balance, setBalance] = useState(0);
  const [ledger, setLedger] = useState([]);

  const [entryType, setEntryType] = useState("topup");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const isAllowed =
    Number(userData?.role_id) === 34 || Number(userData?.role_id) === 35;

  const post = (body) => {
    config.method = "POST";
    config.url = paymentCashAPI;
    config.data = JSON.stringify(body);
    return axios(config);
  };

  const tempRefs = useRef(false);
  useEffect(() => {
    if (tempRefs.current === false && isAllowed) {
      post({ intType: 4 })
        .then((r) => setEmployees(r.data.result || []))
        .catch((e) => console.log(e));
      tempRefs.current = true;
    }
  }, []);

  const loadBalance = (id) =>
    post({ intType: 1, intEmpID: id })
      .then((r) => {
        const row = r.data.result && r.data.result[0];
        setBalance(row ? Number(row.balance) || 0 : 0);
      })
      .catch((e) => console.log(e));

  const loadLedger = (id) =>
    post({ intType: 2, intEmpID: id, intLimit: 50, intOffset: 0 })
      .then((r) => setLedger(r.data.result || []))
      .catch((e) => console.log(e));

  const handleSelectEmployee = (id) => {
    setEmpId(id);
    setBalance(0);
    setLedger([]);
    if (id) {
      loadBalance(id);
      loadLedger(id);
    }
  };

  const alert = (message, messageType) =>
    childRef.current.triggerAlert(JSON.stringify({ message, messageType }));

  const handleAdd = () => {
    if (!empId) return alert("Select an employee first", "error");
    const amt = Number(amount);
    if (!amt) return alert("Enter a valid amount", "error");
    setSaving(true);
    post({
      intType: 3,
      intEmpID: empId,
      txtEntryType: entryType,
      intAmount: amt,
      txtNote: note,
      intUserID: userData.emp_id,
    })
      .then((r) => {
        setSaving(false);
        if (r.data.success) {
          alert(r.data.message || "Saved", "success");
          setAmount("");
          setNote("");
          loadBalance(empId);
          loadLedger(empId);
        } else {
          alert(r.data.message || "Something went wrong", "error");
        }
      })
      .catch((e) => {
        setSaving(false);
        console.log(e);
        alert("Something went wrong", "error");
      });
  };

  return (
    <Layout>
      <Grid className="employee-form bold-header">
        <Grid container spacing={3}>
          <Grid textAlign="center" item className="form-lable" xs={12} lg={6} md={6}>
            <Typography className="page_title" textAlign="left">
              Cash in Hand
            </Typography>
            <Breadcrumbs aria-label="breadcrumb">
              <Link underline="hover" color="inherit" href="/dashboard">
                Dashboard
              </Link>
              <Typography color="text.primary">Cash in Hand</Typography>
            </Breadcrumbs>
          </Grid>
        </Grid>

        <div className="empty-space-40"></div>

        {!isAllowed ? (
          <Typography color="error" sx={{ mt: 2 }}>
            You are not authorized to view this page.
          </Typography>
        ) : (
          <>
            <Grid container spacing={2} margin={`10px 0px`} padding={`0px 15px 0px 0px`}>
              <Grid item xs={12} lg={5} md={5}>
                <FormControl fullWidth>
                  <InputLabel id="emp-select-label">Site engineer</InputLabel>
                  <Select
                    labelId="emp-select-label"
                    value={empId}
                    label="Site engineer"
                    onChange={(e) => handleSelectEmployee(e.target.value)}
                  >
                    <MenuItem value={0}>
                      <em>Select employee</em>
                    </MenuItem>
                    {employees.map((row) => (
                      <MenuItem value={row.emp_id} key={row.emp_id}>
                        {row.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {empId > 0 && (
                <Grid item xs={12} lg={4} md={4}>
                  <Card variant="outlined" sx={{ borderRadius: 2, backgroundColor: "#f4f6fb" }}>
                    <CardContent sx={{ py: 1.5 }}>
                      <Typography sx={{ fontSize: 12, color: "#7d8f8e", fontWeight: 600 }}>
                        Balance in hand
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 22,
                          fontWeight: 800,
                          color: balance < 0 ? "#c62828" : "#17706e",
                        }}
                      >
                        {inr(balance)}
                      </Typography>
                      {balance < 0 && (
                        <Typography sx={{ fontSize: 11.5, color: "#c62828" }}>
                          Overspent — reimbursement due
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>

            {empId > 0 && (
              <>
                <Typography sx={{ fontWeight: 700, mt: 2, mb: 1, color: "#334" }}>
                  Add entry
                </Typography>
                <Grid container spacing={2} padding={`0px 15px 0px 0px`}>
                  <Grid item xs={12} lg={3} md={3}>
                    <FormControl fullWidth>
                      <InputLabel id="type-select-label">Type</InputLabel>
                      <Select
                        labelId="type-select-label"
                        value={entryType}
                        label="Type"
                        onChange={(e) => setEntryType(e.target.value)}
                      >
                        {ENTRY_TYPES.map((t) => (
                          <MenuItem value={t.value} key={t.value}>
                            {t.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} lg={3} md={3}>
                    <TextField
                      fullWidth
                      label="Amount"
                      type="number"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      helperText={
                        entryType === "adjustment" ? "Use a negative value to deduct" : " "
                      }
                    />
                  </Grid>
                  <Grid item xs={12} lg={4} md={4}>
                    <TextField
                      fullWidth
                      label="Note"
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} lg={2} md={2} sx={{ display: "flex", alignItems: "flex-start" }}>
                    <LoadingButton
                      size="large"
                      variant="contained"
                      color="primary"
                      loading={saving}
                      onClick={handleAdd}
                    >
                      Save
                    </LoadingButton>
                  </Grid>
                </Grid>

                <div className="empty-space-40"></div>
                <Typography sx={{ fontWeight: 700, mb: 1.5, color: "#334" }}>
                  Ledger
                </Typography>
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{ borderRadius: 2, maxWidth: 1000 }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "#f4f6fb" }}>
                        <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="right">
                          Amount
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="right">
                          Balance
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Note</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>By</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {ledger.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 3, color: "text.secondary" }}>
                            No entries yet.
                          </TableCell>
                        </TableRow>
                      ) : (
                        ledger.map((row) => {
                          const debit = row.entry_type === "payment";
                          return (
                            <TableRow key={row.id} hover>
                              <TableCell>
                                {row.created_at ? moment(row.created_at).format("DD MMM YYYY, hh:mm A") : "—"}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={row.entry_type}
                                  color={debit ? "warning" : "success"}
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell align="right" sx={{ color: debit ? "#c62828" : "#2E7D32", fontWeight: 700 }}>
                                {debit ? "−" : "+"}
                                {inr(Math.abs(Number(row.amount) || 0))}
                              </TableCell>
                              <TableCell align="right">{inr(row.balance_after)}</TableCell>
                              <TableCell>{row.note || "—"}</TableCell>
                              <TableCell>{row.created_by_name || "—"}</TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </>
        )}
      </Grid>
      <AlertMessage ref={childRef} />
    </Layout>
  );
}

export default CashInHand;
